import KoreanLunarCalendar from 'korean-lunar-calendar';
import { addDays, parseISODate, toISODate } from '../utils/date';
import { supabase } from '../supabaseClient';
import { AcademicTerm, AcademicYearSettings, CalendarDay, DayKind } from '../types/domain';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { canWriteSchoolScopedDataCache, invalidateSchoolScopedDataCaches } from '../utils/schoolDataCacheVersion';
import { deviceCacheStorage } from '../utils/deviceCacheStorage';

const CALENDAR_CACHE_VERSION = 'v1';

function termsCacheKey(workspaceId: string, academicYear: number): string {
  return `cp.academicTerms.${CALENDAR_CACHE_VERSION}.${workspaceId}.${academicYear}`;
}

function calendarDaysCacheKey(workspaceId: string, from: string, to: string): string {
  return `cp.calendarDays.${CALENDAR_CACHE_VERSION}.${workspaceId}.${from}.${to}`;
}

function academicYearSettingsCacheKey(workspaceId: string, academicYear: number): string {
  return `cp.academicYearSettings.${CALENDAR_CACHE_VERSION}.${workspaceId}.${academicYear}`;
}

/** 네트워크 없이 학년도 설정 LS만 읽는다 (메인 프레젠 등). */
export function loadAcademicYearSettingsCache(
  workspaceId: string,
  academicYear: number
): AcademicYearSettings | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(academicYearSettingsCacheKey(workspaceId, academicYear));
    if (raw == null || raw === '') return null;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') return parsed as AcademicYearSettings;
  } catch {
    /* ignore */
  }
  return null;
}

const pendingAcademicYearSettings = new Map<string, Promise<AcademicYearSettings | null>>();

function removeCalendarDaysCacheBySchool(workspaceId: string): void {
  if (!workspaceId || typeof window === 'undefined') return;
  const prefix = `cp.calendarDays.${CALENDAR_CACHE_VERSION}.${workspaceId}.`;
  try {
    const keys: string[] = [];
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const k = deviceCacheStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => deviceCacheStorage.removeItem(k));
  } catch {
    // ignore localStorage errors
  }
}

export type GetAcademicYearSettingsOptions = { skipLocalCache?: boolean };

export async function getAcademicYearSettings(
  workspaceId: string,
  academicYear: number,
  options?: GetAcademicYearSettingsOptions
): Promise<AcademicYearSettings | null> {
  if (!options?.skipLocalCache && workspaceId && typeof window !== 'undefined') {
    try {
      const raw = deviceCacheStorage.getItem(academicYearSettingsCacheKey(workspaceId, academicYear));
      if (raw != null && raw !== '') {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object') {
          recordDevLocalStorageProgramCacheUse('학년도설정·LS');
          return parsed as AcademicYearSettings;
        }
      }
    } catch {
      /* ignore */
    }
  }

  const inflightKey = `${workspaceId}|${academicYear}|${options?.skipLocalCache ? 'force' : 'def'}`;
  const inflight = pendingAcademicYearSettings.get(inflightKey);
  if (inflight) return inflight;

  const task = (async () => {
    try {
      const { data, error } = await supabase
        .from('academic_year_settings')
        .select(
          'id, workspace_id, academic_year, year_starts_on, year_ends_on, summer_break_starts_on, summer_break_ends_on, winter_break_starts_on, winter_break_ends_on'
        )
        .eq('workspace_id', workspaceId)
        .eq('academic_year', academicYear)
        .maybeSingle();

      if (error) {
        if (error.message?.includes("Could not find the table 'public.academic_year_settings'")) {
          return null;
        }
        throw new Error(error.message);
      }
      const row = data as AcademicYearSettings | null;
      if (row && workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
        try {
          deviceCacheStorage.setItem(
            academicYearSettingsCacheKey(workspaceId, academicYear),
            JSON.stringify(row)
          );
        } catch {
          /* ignore quota */
        }
      }
      return row;
    } finally {
      pendingAcademicYearSettings.delete(inflightKey);
    }
  })();

  pendingAcademicYearSettings.set(inflightKey, task);
  return task;
}

export async function upsertAcademicYearSettings(
  workspaceId: string,
  payload: {
    academicYear: number;
    yearStartsOn: string;
    yearEndsOn: string;
    summerBreakStartsOn: string;
    summerBreakEndsOn: string;
    winterBreakStartsOn?: string | null;
    winterBreakEndsOn?: string | null;
  }
): Promise<AcademicYearSettings> {
  const { data, error } = await supabase
    .from('academic_year_settings')
    .upsert(
      {
        workspace_id: workspaceId,
        academic_year: payload.academicYear,
        year_starts_on: payload.yearStartsOn,
        year_ends_on: payload.yearEndsOn,
        summer_break_starts_on: payload.summerBreakStartsOn,
        summer_break_ends_on: payload.summerBreakEndsOn,
        winter_break_starts_on: payload.winterBreakStartsOn ?? null,
        winter_break_ends_on: payload.winterBreakEndsOn ?? null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'workspace_id,academic_year' }
    )
    .select('id, workspace_id, academic_year, year_starts_on, year_ends_on, summer_break_starts_on, summer_break_ends_on, winter_break_starts_on, winter_break_ends_on')
    .single();

  if (error || !data) {
    if (error?.message?.includes("Could not find the table 'public.academic_year_settings'")) {
      throw new Error('학사일정 설정을 사용하려면 DB에 academic_year_settings 테이블이 필요합니다. Supabase 마이그레이션(20260316_001_academic_year_settings.sql)을 적용해 주세요.');
    }
    throw new Error(error?.message ?? '학사일정 설정 저장에 실패했습니다.');
  }

  const settings = data as AcademicYearSettings;
  const term1End = addDays(parseISODate(payload.summerBreakStartsOn), -1);
  const term2Start = addDays(parseISODate(payload.summerBreakEndsOn), 1);
  try {
    await upsertAcademicTermRow(workspaceId, {
      academicYear: payload.academicYear,
      termNumber: 1,
      startsOn: payload.yearStartsOn,
      endsOn: toISODate(term1End),
    });
    await upsertAcademicTermRow(workspaceId, {
      academicYear: payload.academicYear,
      termNumber: 2,
      startsOn: toISODate(term2Start),
      endsOn: payload.yearEndsOn,
    });
  } catch (termError: any) {
    throw new Error(termError?.message ?? '학기 일정 반영에 실패했습니다.');
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['academicCalendar']);
  if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
    try {
      deviceCacheStorage.setItem(
        academicYearSettingsCacheKey(workspaceId, payload.academicYear),
        JSON.stringify(settings)
      );
    } catch {
      /* ignore quota */
    }
  }
  return settings;
}

export type GetAcademicTermsOptions = { skipLocalCache?: boolean };

export async function getAcademicTerms(
  workspaceId: string,
  academicYear: number,
  options?: GetAcademicTermsOptions
): Promise<AcademicTerm[]> {
  if (!options?.skipLocalCache && workspaceId && typeof window !== 'undefined') {
    try {
      const cached = deviceCacheStorage.getItem(termsCacheKey(workspaceId, academicYear));
      if (cached) {
        const parsed = JSON.parse(cached) as unknown;
        if (Array.isArray(parsed)) return parsed as AcademicTerm[];
      }
    } catch {
      // ignore parse failure
    }
  }
  const { data, error } = await supabase
    .from('academic_terms')
    .select('id, workspace_id, academic_year, term_number, starts_on, ends_on')
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .order('term_number', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }
  const rows = (data ?? []) as AcademicTerm[];
  if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
    try {
      deviceCacheStorage.setItem(termsCacheKey(workspaceId, academicYear), JSON.stringify(rows));
    } catch {
      // ignore quota
    }
  }
  return rows;
}

async function upsertAcademicTermRow(
  workspaceId: string,
  payload: {
    academicYear: number;
    termNumber: 1 | 2;
    startsOn: string;
    endsOn: string;
  }
): Promise<AcademicTerm> {
  const { data, error } = await supabase
    .from('academic_terms')
    .upsert(
      {
        workspace_id: workspaceId,
        academic_year: payload.academicYear,
        term_number: payload.termNumber,
        starts_on: payload.startsOn,
        ends_on: payload.endsOn,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'workspace_id,academic_year,term_number' }
    )
    .select('id, workspace_id, academic_year, term_number, starts_on, ends_on')
    .single();

  if (error || !data) {
    throw new Error(error?.message ?? '학기 정보 저장에 실패했습니다.');
  }
  return data as AcademicTerm;
}

export async function upsertAcademicTerm(
  workspaceId: string,
  payload: {
    academicYear: number;
    termNumber: 1 | 2;
    startsOn: string;
    endsOn: string;
  }
): Promise<AcademicTerm> {
  const saved = await upsertAcademicTermRow(workspaceId, payload);
  await invalidateSchoolScopedDataCaches(workspaceId, ['academicCalendar']);
  return saved;
}

export function loadCalendarDaysLocalCache(
  workspaceId: string,
  from: string,
  to: string
): { found: boolean; rows: CalendarDay[] } {
  if (!workspaceId || !from || !to || typeof window === 'undefined') {
    return { found: false, rows: [] };
  }
  try {
    const cached = deviceCacheStorage.getItem(calendarDaysCacheKey(workspaceId, from, to));
    if (!cached) return { found: false, rows: [] };
    const parsed = JSON.parse(cached) as unknown;
    if (!Array.isArray(parsed)) return { found: false, rows: [] };
    return { found: true, rows: parsed as CalendarDay[] };
  } catch {
    return { found: false, rows: [] };
  }
}

/** 워크스페이스의 학사일정 `calendar_days` LS 캐시(구간 키 무관)를 모두 합친다. */
export function loadAllCalendarDaysLocalCaches(workspaceId: string): CalendarDay[] {
  if (!workspaceId || typeof window === 'undefined') return [];
  const prefix = `cp.calendarDays.${CALENDAR_CACHE_VERSION}.${workspaceId}.`;
  const out: CalendarDay[] = [];
  try {
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const k = deviceCacheStorage.key(i);
      if (!k || !k.startsWith(prefix)) continue;
      const raw = deviceCacheStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) out.push(...(parsed as CalendarDay[]));
    }
  } catch {
    /* ignore */
  }
  return out;
}

export async function getCalendarDays(
  workspaceId: string,
  from: string,
  to: string
): Promise<CalendarDay[]> {
  if (workspaceId && from && to && typeof window !== 'undefined') {
    try {
      const cached = deviceCacheStorage.getItem(calendarDaysCacheKey(workspaceId, from, to));
      if (cached) {
        const parsed = JSON.parse(cached) as unknown;
        if (Array.isArray(parsed)) return parsed as CalendarDay[];
      }
    } catch {
      // ignore parse failure
    }
  }
  const { data, error } = await supabase
    .from('calendar_days')
    .select('id, workspace_id, day, day_kind, note')
    .eq('workspace_id', workspaceId)
    .gte('day', from)
    .lte('day', to)
    .order('day', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }
  const rows = (data ?? []) as CalendarDay[];
  if (workspaceId && from && to && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
    try {
      deviceCacheStorage.setItem(calendarDaysCacheKey(workspaceId, from, to), JSON.stringify(rows));
    } catch {
      // ignore quota
    }
  }
  return rows;
}

async function upsertCalendarDayRow(
  workspaceId: string,
  payload: { day: string; dayKind: DayKind; note?: string }
): Promise<void> {
  const { error } = await supabase
    .from('calendar_days')
    .upsert(
      {
        workspace_id: workspaceId,
        day: payload.day,
        day_kind: payload.dayKind,
        note: payload.note?.trim() || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'workspace_id,day' }
    );

  if (error) {
    throw new Error(error.message);
  }
}

export async function upsertCalendarDay(
  workspaceId: string,
  payload: { day: string; dayKind: DayKind; note?: string }
): Promise<void> {
  await upsertCalendarDayRow(workspaceId, payload);
  await invalidateSchoolScopedDataCaches(workspaceId, ['academicCalendar']);
}

export async function deleteCalendarDay(workspaceId: string, day: string): Promise<void> {
  const { error } = await supabase
    .from('calendar_days')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('day', day);

  if (error) {
    throw new Error(error.message);
  }
  removeCalendarDaysCacheBySchool(workspaceId);
  await invalidateSchoolScopedDataCaches(workspaceId, ['academicCalendar']);
}

/** 해당 연도 한국 태양력 공휴일 목록 (고정일) */
export function getKoreanSolarHolidays(year: number): { day: string; note: string }[] {
  return [
    { day: `${year}-01-01`, note: '신정' },
    { day: `${year}-03-01`, note: '3.1절' },
    { day: `${year}-05-01`, note: '노동절' },
    { day: `${year}-05-05`, note: '어린이날' },
    { day: `${year}-06-06`, note: '현충일' },
    { day: `${year}-07-17`, note: '제헌절' },
    { day: `${year}-08-15`, note: '광복절' },
    { day: `${year}-10-03`, note: '개천절' },
    { day: `${year}-10-09`, note: '한글날' },
    { day: `${year}-12-25`, note: '크리스마스' },
  ];
}

type HolidayItem = { day: string; note: string };

type LunarHolidaySpec = {
  month: number;
  day: number;
  label: string;
  offsets?: number[];
};

const LUNAR_HOLIDAY_SPECS: LunarHolidaySpec[] = [
  { month: 1, day: 1, label: '설날', offsets: [-1, 0, 1] },
  { month: 4, day: 8, label: '부처님오신날' },
  { month: 8, day: 15, label: '추석', offsets: [-1, 0, 1] },
];

/** 대체공휴일 적용 대상(단일일): 3.1절·어린이날·광복절·개천절·한글날·부처님오신날·크리스마스·제헌절 */
const SUBSTITUTE_SINGLE_HOLIDAY_NOTES = new Set([
  '3.1절',
  '어린이날',
  '광복절',
  '개천절',
  '한글날',
  '부처님오신날',
  '크리스마스',
  '제헌절',
]);
/** 설날·추석 각 3일 연휴에 대한 대체공휴일(연휴 중 주말·다른 공휴일과 겹칠 때) */
const SUBSTITUTE_GROUP_HOLIDAY_NOTES = new Set(['설날', '추석']);

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function holidayDateKey(day: string): string {
  return day;
}

function toHolidayMap(list: HolidayItem[]): Map<string, HolidayItem> {
  return new Map(list.map((item) => [holidayDateKey(item.day), item] as const));
}

function isHolidayDate(day: string, map: Map<string, HolidayItem>): boolean {
  return map.has(holidayDateKey(day));
}

function isWeekendDate(day: string): boolean {
  const weekDay = parseISODate(day).getDay();
  return weekDay === 0 || weekDay === 6;
}

function convertLunarToSolar(
  lunarYear: number,
  lunarMonth: number,
  lunarDay: number,
  intercalation = false
): string | null {
  const calendar = new KoreanLunarCalendar();
  const ok = calendar.setLunarDate(lunarYear, lunarMonth, lunarDay, intercalation);
  if (!ok) return null;
  const solar = calendar.getSolarCalendar() as { year: number; month: number; day: number };
  if (!solar?.year || !solar?.month || !solar?.day) return null;
  return `${solar.year}-${pad2(solar.month)}-${pad2(solar.day)}`;
}

function getKoreanLunarHolidayCandidates(year: number): HolidayItem[] {
  const out: HolidayItem[] = [];
  const candidateYears = [year - 1, year, year + 1];
  LUNAR_HOLIDAY_SPECS.forEach((spec) => {
    candidateYears.forEach((lunarYear) => {
      const base = convertLunarToSolar(lunarYear, spec.month, spec.day, false);
      if (!base) return;
      const baseDate = parseISODate(base);
      (spec.offsets ?? [0]).forEach((offset) => {
        out.push({
          day: toISODate(addDays(baseDate, offset)),
          note: spec.label,
        });
      });
    });
  });
  return out;
}

function computeSubstituteHoliday(baseHolidays: HolidayItem[]): HolidayItem[] {
  const holidayMap = toHolidayMap(baseHolidays);
  const dayCountMap = new Map<string, number>();
  baseHolidays.forEach((holiday) => {
    dayCountMap.set(holiday.day, (dayCountMap.get(holiday.day) ?? 0) + 1);
  });
  const substitutes: HolidayItem[] = [];
  const addedStarts = new Set<string>();

  const addSubstitute = (startDay: string) => {
    if (addedStarts.has(startDay)) return;
    addedStarts.add(startDay);
    let cursor = addDays(parseISODate(startDay), 1);
    while (true) {
      const candidate = toISODate(cursor);
      if (!isWeekendDate(candidate) && !isHolidayDate(candidate, holidayMap)) {
        const substitute: HolidayItem = { day: candidate, note: '대체공휴일' };
        holidayMap.set(candidate, substitute);
        substitutes.push(substitute);
        return;
      }
      cursor = addDays(cursor, 1);
    }
  };

  baseHolidays.forEach((holiday) => {
    if (!SUBSTITUTE_SINGLE_HOLIDAY_NOTES.has(holiday.note)) return;
    if (isWeekendDate(holiday.day) || (dayCountMap.get(holiday.day) ?? 0) > 1) {
      addSubstitute(holiday.day);
    }
  });

  Array.from(SUBSTITUTE_GROUP_HOLIDAY_NOTES).forEach((note) => {
    const days = baseHolidays
      .filter((holiday) => holiday.note === note)
      .map((holiday) => holiday.day)
      .sort((a, b) => a.localeCompare(b));
    if (days.length === 0) return;
    const hasOverlap = days.some((day) => isWeekendDate(day) || (dayCountMap.get(day) ?? 0) > 1);
    if (!hasOverlap) return;
    addSubstitute(days[days.length - 1]!);
  });

  return substitutes;
}

export function getKoreanAutoHolidays(year: number): HolidayItem[] {
  const mergedMap = new Map<string, HolidayItem>();
  [...getKoreanSolarHolidays(year), ...getKoreanLunarHolidayCandidates(year)].forEach((item) => {
    if (!mergedMap.has(item.day)) mergedMap.set(item.day, item);
  });
  const base = Array.from(mergedMap.values()).sort((a, b) => a.day.localeCompare(b.day));
  const substitutes = computeSubstituteHoliday(base);
  return [...base, ...substitutes].sort((a, b) => a.day.localeCompare(b.day));
}

/** 여러 연도의 자동 공휴일 목록을 합칠 때 같은 양력일이 중복되지 않게 함 (학년도가 두 해에 걸칠 때 추석·설 등) */
export function dedupeHolidaysByDay<T extends { day: string }>(items: T[]): T[] {
  const map = new Map<string, T>();
  items.forEach((item) => {
    if (!map.has(item.day)) map.set(item.day, item);
  });
  return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
}

/** 학년도 구간(yearStartsOn ~ yearEndsOn) 안에 있는 공휴일만 일괄 등록 */
export async function registerKoreanHolidaysInRange(
  workspaceId: string,
  yearStartsOn: string,
  yearEndsOn: string
): Promise<number> {
  const [y1] = yearStartsOn.split('-').map(Number);
  const [y2] = yearEndsOn.split('-').map(Number);
  const years = y1 === y2 ? [y1] : [y1, y2];
  const all: HolidayItem[] = [];
  years.forEach((y) => all.push(...getKoreanAutoHolidays(y)));
  const merged = dedupeHolidaysByDay(all);
  const inRange = merged.filter(
    (h) => h.day >= yearStartsOn && h.day <= yearEndsOn
  );
  for (const h of inRange) {
    await upsertCalendarDayRow(workspaceId, { day: h.day, dayKind: 'holiday', note: h.note });
  }
  if (inRange.length > 0) {
    await invalidateSchoolScopedDataCaches(workspaceId, ['academicCalendar']);
  }
  return inRange.length;
}

